import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiPlus, FiEdit, FiTrash2, FiImage, FiX } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUtils';

const GerenciarGaleriaFotos = () => {
  const [fotos, setFotos] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [fotoEdit, setFotoEdit] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    ordem: 0,
    viagem_id: ''
  });
  const [fotoPreview, setFotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fotosPorViagem, setFotosPorViagem] = useState({});

  useEffect(() => {
    fetchFotos();
    fetchViagens();
  }, []);

  const fetchFotos = async () => {
    try {
      const response = await api.get('/api/site/galeria-fotos');
      setFotos(response.data);
      
      // Contar fotos por viagem
      const contagem = {};
      response.data.forEach(foto => {
        if (foto.viagem_id) {
          contagem[foto.viagem_id] = (contagem[foto.viagem_id] || 0) + 1;
        }
      });
      setFotosPorViagem(contagem);
    } catch (error) {
      toast.error('Erro ao carregar fotos');
    } finally {
      setLoading(false);
    }
  };

  const fetchViagens = async () => {
    try {
      const response = await api.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titulo', formData.titulo);
      formDataToSend.append('descricao', formData.descricao);
      formDataToSend.append('ordem', formData.ordem);
      if (formData.viagem_id) {
        formDataToSend.append('viagem_id', formData.viagem_id);
      }

      if (fotoEdit) {
        // Se houver nova foto, adicionar ao FormData
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput && fileInput.files[0]) {
          formDataToSend.append('foto', fileInput.files[0]);
        }
        await api.put(`/api/site/galeria-fotos/${fotoEdit.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Foto atualizada com sucesso');
      } else {
        const fileInput = document.querySelector('input[type="file"]');
        if (!fileInput || !fileInput.files[0]) {
          toast.error('Selecione uma foto');
          setUploading(false);
          return;
        }
        formDataToSend.append('foto', fileInput.files[0]);
        await api.post('/api/site/galeria-fotos', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Foto adicionada com sucesso');
      }

      setModalOpen(false);
      setFotoEdit(null);
      setFormData({ titulo: '', descricao: '', ordem: 0, viagem_id: '' });
      setFotoPreview(null);
      fetchFotos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta foto?')) return;

    try {
      await api.delete(`/api/site/galeria-fotos/${id}`);
      toast.success('Foto excluída com sucesso');
      fetchFotos();
    } catch (error) {
      toast.error('Erro ao excluir foto');
    }
  };

  const handleEdit = (foto) => {
    setFotoEdit(foto);
    setFormData({
      titulo: foto.titulo || '',
      descricao: foto.descricao || '',
      ordem: foto.ordem || 0,
      viagem_id: foto.viagem_id || ''
    });
    setFotoPreview(getImageUrl(foto.url));
    setModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white">Gerenciar - Galeria de Fotos</h1>
          <p className="text-white mt-1">Adicione e gerencie as fotos da galeria</p>
        </div>
        <button
          onClick={() => {
            setFotoEdit(null);
            setFormData({ titulo: '', descricao: '', ordem: 0, viagem_id: '' });
            setFotoPreview(null);
            setModalOpen(true);
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="mr-2" size={20} />
          Adicionar Foto
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {fotos.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-sky-50 rounded-lg">
              <FiImage className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">Nenhuma foto cadastrada</p>
            </div>
          ) : (
            fotos.map((foto) => (
              <div key={foto.id} className="bg-sky-50 rounded-lg overflow-hidden shadow">
                <div className="relative aspect-square bg-gray-200">
                  <img
                    src={getImageUrl(foto.url)}
                    alt={foto.titulo || 'Foto'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={() => handleEdit(foto)}
                      className="bg-white/90 hover:bg-white p-2 rounded-full text-sky-600"
                    >
                      <FiEdit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(foto.id)}
                      className="bg-white/90 hover:bg-white p-2 rounded-full text-red-600"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="p-3">
                  {foto.viagem_titulo && (
                    <p className="text-xs text-sky-600 font-medium mb-1">{foto.viagem_titulo}</p>
                  )}
                  {foto.titulo && (
                    <p className="text-sm font-medium text-gray-900 truncate">{foto.titulo}</p>
                  )}
                  {foto.ordem !== null && (
                    <p className="text-xs text-gray-500">Ordem: {foto.ordem}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {fotoEdit ? 'Editar Foto' : 'Adicionar Foto'}
              </h2>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setFotoEdit(null);
                  setFotoPreview(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {fotoEdit ? 'Nova Foto (opcional)' : 'Foto *'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  required={!fotoEdit}
                />
                {fotoPreview && (
                  <div className="mt-2 relative">
                    <img
                      src={fotoPreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Viagem (opcional)</label>
                <select
                  value={formData.viagem_id}
                  onChange={(e) => setFormData({ ...formData, viagem_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 bg-white text-gray-800"
                >
                  <option value="">Sem viagem específica</option>
                  {viagens.map((viagem) => {
                    const fotosNaViagem = fotosPorViagem[viagem.id] || 0;
                    const podeAdicionar = !formData.viagem_id || formData.viagem_id === String(viagem.id) || fotosNaViagem < 10;
                    return (
                      <option 
                        key={viagem.id} 
                        value={viagem.id}
                        disabled={!podeAdicionar && !fotoEdit}
                      >
                        {viagem.titulo} - {fotosNaViagem}/10 fotos
                      </option>
                    );
                  })}
                </select>
                {formData.viagem_id && fotosPorViagem[formData.viagem_id] >= 10 && !fotoEdit && (
                  <p className="text-sm text-red-600 mt-1">Limite de 10 fotos por viagem atingido</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                <input
                  type="text"
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                <input
                  type="number"
                  value={formData.ordem}
                  onChange={(e) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min="0"
                />
              </div>
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setModalOpen(false);
                    setFotoEdit(null);
                    setFotoPreview(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={uploading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  disabled={uploading}
                >
                  {uploading ? 'Salvando...' : fotoEdit ? 'Atualizar' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerenciarGaleriaFotos;
